import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { authApi } from '@/services/api';
import { useCurrentUser } from '@/hooks/useQueries';
import { performLogout } from '@/utils/auth.ts';

export default function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [showNotification, setShowNotification] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTestResult, setShowTestResult] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);

  // Add missing state variables
  const [lowStockThreshold, setLowStockThreshold] = useState(20);
  const [criticalStockThreshold, setCriticalStockThreshold] = useState(10);
  const [expiryWarningDays, setExpiryWarningDays] = useState(30);
  const [reorderMultiplier, setReorderMultiplier] = useState(1.5);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lowStockAlertsEnabled, setLowStockAlertsEnabled] = useState(true);
  const [transactionUpdatesEnabled, setTransactionUpdatesEnabled] = useState(true);

  const [thresholds, setThresholds] = useState({
    lowStock: 20,
    criticalStock: 10,
    expiryWarning: 30,
    reorderMultiplier: 1.5
  });

  const [emailSettings, setEmailSettings] = useState({
    email: '',
    enableAlerts: true,
    alertFrequency: 'immediate'
  });

  const queryClient = useQueryClient();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const tabs = [
    { id: 'profile', name: 'Profile', icon: 'ri-user-line' },
    { id: 'notifications', name: 'Notifications', icon: 'ri-notification-3-line' },
    { id: 'security', name: 'Security', icon: 'ri-shield-line' }
  ];

  // Update profile data from React Query
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
      });
      setLoading(false);
    } else if (userLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [currentUser, userLoading]);

  useEffect(() => {
    const saved = localStorage.getItem('stockThresholds');
    if (saved) {
      const savedThresholds = JSON.parse(saved);
      setThresholds(savedThresholds);
      setLowStockThreshold(savedThresholds.lowStock || 20);
      setCriticalStockThreshold(savedThresholds.criticalStock || 10);
      setExpiryWarningDays(savedThresholds.expiryWarning || 30);
      setReorderMultiplier(savedThresholds.reorderMultiplier || 1.5);
    }

    const savedEmail = localStorage.getItem('emailSettings');
    if (savedEmail) {
      setEmailSettings(JSON.parse(savedEmail));
    }

    // Load auto-refresh setting from localStorage
    const savedAutoRefresh = localStorage.getItem('autoRefreshDashboard');
    if (savedAutoRefresh !== null) {
      setAutoRefresh(savedAutoRefresh === 'true');
    }

    // Load notification preferences from localStorage
    const savedNotifications = localStorage.getItem('notificationPreferences');
    if (savedNotifications) {
      try {
        const prefs = JSON.parse(savedNotifications);
        setLowStockAlertsEnabled(prefs.lowStockAlerts !== false); // Default to true
        setTransactionUpdatesEnabled(prefs.transactionUpdates !== false); // Default to true
      } catch (error) {
        console.error('Error parsing notification preferences:', error);
      }
    }
  }, []);

  const handleSaveSettings = () => {
    const updatedThresholds = {
      lowStock: lowStockThreshold,
      criticalStock: criticalStockThreshold,
      expiryWarning: expiryWarningDays,
      reorderMultiplier: reorderMultiplier
    };
    localStorage.setItem('stockThresholds', JSON.stringify(updatedThresholds));
    setThresholds(updatedThresholds);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('stockThresholds', JSON.stringify(thresholds));
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleSaveEmailSettings = () => {
    if (!emailSettings.email) {
      alert('Please enter an email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailSettings.email)) {
      alert('Please enter a valid email address');
      return;
    }

    localStorage.setItem('emailSettings', JSON.stringify(emailSettings));
    setShowEmailModal(false);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleTestConnection = () => {
    // Simulate testing email connection
    setTestSuccess(Math.random() > 0.2); // 80% success rate for demo
    setShowTestResult(true);
    setTimeout(() => {
      setShowTestResult(false);
    }, 4000);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdatingProfile(true);
    setProfileError('');

    try {
      await authApi.updateProfile({
        name: profileData.name.trim(),
        email: profileData.email.trim(),
      });
      
      // Invalidate and refetch current user data
      queryClient.invalidateQueries({ queryKey: ['auth', 'currentUser'] });
      
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    } catch (err: any) {
      setProfileError(err.message || 'Failed to update profile');
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdatingPassword(true);
    setPasswordError('');

    if (passwordData.password !== passwordData.password_confirmation) {
      setPasswordError('Passwords do not match');
      setUpdatingPassword(false);
      return;
    }

    if (passwordData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      setUpdatingPassword(false);
      return;
    }

    try {
      await authApi.updatePassword(
        passwordData.current_password,
        passwordData.password,
        passwordData.password_confirmation
      );
      
      // Clear auth data and cache, then force re-login
      await performLogout(queryClient, (path) => {
        navigate(path, { 
          replace: true,
          state: { message: 'Password updated successfully. Please login again with your new password.' }
        });
      });
      
      // Show success message briefly before redirecting
      setShowNotification(true);
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { 
          replace: true,
          state: { message: 'Password updated successfully. Please login again with your new password.' }
        });
      }, 1500);
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to update password');
      setUpdatingPassword(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Success Notification */}
      {showNotification && (
        <div className="fixed top-6 right-6 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 flex items-center gap-3 animate-slide-in">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-green-600"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Settings Saved</p>
            <p className="text-xs text-gray-500">Your preferences have been updated</p>
          </div>
          <button
            onClick={() => setShowNotification(false)}
            className="ml-4 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Test Connection Result */}
      {showTestResult && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${
            testSuccess 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              testSuccess ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <i className={`${
                testSuccess ? 'ri-check-line text-green-600' : 'ri-close-line text-red-600'
              } text-lg`}></i>
            </div>
            <div>
              <p className={`text-sm font-medium ${
                testSuccess ? 'text-green-900' : 'text-red-900'
              }`}>
                {testSuccess ? 'Connection Successful' : 'Connection Failed'}
              </p>
              <p className={`text-xs ${
                testSuccess ? 'text-green-600' : 'text-red-600'
              }`}>
                {testSuccess 
                  ? 'Email notifications are working properly' 
                  : 'Please check your email settings'}
              </p>
            </div>
            <button
              onClick={() => setShowTestResult(false)}
              className={`w-6 h-6 flex items-center justify-center rounded hover:bg-white/50 cursor-pointer ${
                testSuccess ? 'text-green-600' : 'text-red-600'
              }`}
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Email Configuration Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Email Notification Settings</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="px-6 py-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={emailSettings.email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, email: e.target.value })}
                  placeholder="admin@hic.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Low stock alerts will be sent to this email
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Enable Email Alerts</p>
                  <p className="text-xs text-gray-500 mt-1">Receive notifications for low stock</p>
                </div>
                <button
                  onClick={() => setEmailSettings({ ...emailSettings, enableAlerts: !emailSettings.enableAlerts })}
                  className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                    emailSettings.enableAlerts ? 'bg-[#50b3e1]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      emailSettings.enableAlerts ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  ></span>
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alert Frequency
                </label>
                <select
                  value={emailSettings.alertFrequency}
                  onChange={(e) => setEmailSettings({ ...emailSettings, alertFrequency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] cursor-pointer"
                >
                  <option value="immediate">Immediate</option>
                  <option value="daily">Daily Digest</option>
                  <option value="weekly">Weekly Summary</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmailSettings}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-[#50b3e1] to-[#0997a4] text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your system preferences and configurations</p>
        </div>

        {/* Settings Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1 p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === tab.id
                        ? 'bg-[#eb1539] text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${tab.icon} text-base`}></i>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-4 p-5">
              {activeTab === 'profile' && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h2>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#50b3e1]"></div>
                        <p className="mt-4 text-gray-600 text-sm">Loading profile...</p>
                      </div>
                    ) : (
                      <form onSubmit={handleUpdateProfile} className="space-y-5">
                        {profileError && (
                          <div className="bg-[#ed1438]/10 border border-[#ed1438]/20 rounded-lg p-3 flex items-center gap-2 text-[#ed1438] text-sm">
                            <i className="ri-error-warning-line text-lg"></i>
                            {profileError}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                          <div className="w-16 h-16 bg-gradient-to-br from-[#50b3e1] to-[#0997a4] rounded-full flex items-center justify-center text-white font-semibold text-xl">
                            {currentUser?.name
                              ? currentUser.name
                                  .split(' ')
                                  .map((n: string) => n[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2)
                              : 'U'}
                          </div>
                          <div>
                            <p className="text-base font-semibold text-gray-900">{currentUser?.name || 'User'}</p>
                            <p className="text-sm text-gray-500">{currentUser?.email || 'No email'}</p>
                            <p className="text-xs text-gray-400 mt-0.5 capitalize">{currentUser?.role || 'staff'}</p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={profileData.name}
                              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={profileData.email}
                              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                              required
                            />
                          </div>
                        </div>

                        <div className="pt-4 border-t border-gray-200 flex justify-end">
                          <button
                            type="submit"
                            disabled={updatingProfile}
                            className="px-5 py-2 bg-gradient-to-r from-[#50b3e1] to-[#0997a4] text-white text-sm font-medium rounded-lg hover:shadow-md transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {updatingProfile ? (
                              <span className="flex items-center gap-2">
                                <i className="ri-loader-4-line animate-spin"></i>
                                Updating...
                              </span>
                            ) : (
                              'Update Profile'
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h2>
                  <div className="space-y-3">
                    {/* Low Stock Alerts */}
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Low Stock Alerts</p>
                        <p className="text-xs text-gray-500 mt-0.5">Get notified when items fall below minimum levels</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={lowStockAlertsEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setLowStockAlertsEnabled(newValue);
                            const savedPrefs = localStorage.getItem('notificationPreferences');
                            const prefs = savedPrefs ? JSON.parse(savedPrefs) : {};
                            prefs.lowStockAlerts = newValue;
                            localStorage.setItem('notificationPreferences', JSON.stringify(prefs));
                          }}
                          className="sr-only peer" 
                        />
                        <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#50b3e1]/20 rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#50b3e1]"></div>
                      </label>
                    </div>

                    {/* Expiry Warnings - Not implemented yet */}
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors opacity-60">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Expiry Warnings</p>
                        <p className="text-xs text-gray-500 mt-0.5">Receive alerts for items approaching expiration</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" disabled className="sr-only peer" />
                        <div className="w-10 h-5 bg-gray-200 rounded-full opacity-50 cursor-not-allowed"></div>
                      </label>
                    </div>

                    {/* Transaction Updates */}
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Transaction Updates</p>
                        <p className="text-xs text-gray-500 mt-0.5">Notifications for stock receive and issue activities</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={transactionUpdatesEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setTransactionUpdatesEnabled(newValue);
                            const savedPrefs = localStorage.getItem('notificationPreferences');
                            const prefs = savedPrefs ? JSON.parse(savedPrefs) : {};
                            prefs.transactionUpdates = newValue;
                            localStorage.setItem('notificationPreferences', JSON.stringify(prefs));
                          }}
                          className="sr-only peer" 
                        />
                        <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#50b3e1]/20 rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#50b3e1]"></div>
                      </label>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200 mt-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Dashboard Settings</h2>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                        <div>
                          <span className="text-sm font-medium text-gray-900">Auto-refresh Dashboard</span>
                          <p className="text-xs text-gray-500 mt-1">Automatically update dashboard data every 5 minutes</p>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setAutoRefresh(newValue);
                              localStorage.setItem('autoRefreshDashboard', newValue.toString());
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#50b3e1]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#50b3e1]"></div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>
                  <form onSubmit={handleUpdatePassword} className="space-y-4 max-w-md">
                      {passwordError && (
                        <div className="bg-[#ed1438]/10 border border-[#ed1438]/20 rounded-lg p-3 flex items-center gap-2 text-[#ed1438] text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          {passwordError}
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                        <div className="relative">
                          <input
                            type="password"
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={passwordData.password}
                            onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                            minLength={8}
                            required
                          />
                          <button
                            type="button"
                            onMouseDown={() => setShowNewPassword(true)}
                            onMouseUp={() => setShowNewPassword(false)}
                            onMouseLeave={() => setShowNewPassword(false)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                            tabIndex={-1}
                          >
                            <i className={showNewPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={passwordData.password_confirmation}
                            onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                            minLength={8}
                            required
                          />
                          <button
                            type="button"
                            onMouseDown={() => setShowConfirmPassword(true)}
                            onMouseUp={() => setShowConfirmPassword(false)}
                            onMouseLeave={() => setShowConfirmPassword(false)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                            tabIndex={-1}
                          >
                            <i className={showConfirmPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                          </button>
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={updatingPassword}
                        className="mt-4 px-5 py-2 bg-gradient-to-r from-[#50b3e1] to-[#0997a4] text-white rounded-lg text-sm font-medium hover:shadow-md transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingPassword ? (
                          <span className="flex items-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i>
                            Updating...
                          </span>
                        ) : (
                          'Update Password'
                        )}
                      </button>
                    </form>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
